// src/features/dashboard/components/EmployeeProductivity/HourlyProductivityChart.tsx
import { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
} from "recharts";

import type { HourlyProductivityItem } from "../../types/Employee/employeeProductivity.types";

interface Props {
  data: HourlyProductivityItem[];
  header: {
    title: string;
  };
}

type ProductivityKey = "productive" | "nonProductive" | "neutral";

interface HoveredState {
  index: number | null;
  key: ProductivityKey | null;
}

const CustomLegend = ({
  hoveredKey,
  setHovered,
}: {
  hoveredKey: ProductivityKey | null;
  setHovered: React.Dispatch<React.SetStateAction<HoveredState>>;
}) => (
  <div className="mb-6 flex justify-center gap-6">
    {[
      {
        key: "productive",
        label: "Productive",
        color: "#22C55E",
      },
      {
        key: "nonProductive",
        label: "Non-Prod",
        color: "#EF4444",
      },
      {
        key: "neutral",
        label: "Neutral",
        color: "#94A3B8",
      },
    ].map((item) => (
      <div
        key={item.key}
        className="flex cursor-pointer items-center gap-2"
        onMouseEnter={() =>
          setHovered({
            index: null,
            key: item.key as ProductivityKey,
          })
        }
        onMouseLeave={() =>
          setHovered({
            index: null,
            key: null,
          })
        }
      >
        <span
          className="h-3 w-3 rounded-sm transition-opacity"
          style={{
            background: item.color,
            opacity:
              hoveredKey === null || hoveredKey === item.key ? 1 : 0.3,
          }}
        />

        <span
          className="text-[13px]"
          style={{
            opacity:
              hoveredKey === null || hoveredKey === item.key ? 1 : 0.3,
            fontWeight: hoveredKey === item.key ? 600 : 400,
          }}
        >
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

const CustomTooltip = ({
  active,
  payload,
  label,
  hovered,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey?: string;
    value?: number | string;
  }>;
  label?: string | number;
  hovered: HoveredState;
}) => {
  if (!active || !payload?.length || !hovered.key) return null;

  const current = payload.find((item) => item.dataKey === hovered.key);

  if (!current) return null;

  const colorMap: Record<ProductivityKey, string> = {
    productive: "#22C55E",
    nonProductive: "#EF4444",
    neutral: "#94A3B8",
  };

  const labelMap: Record<ProductivityKey, string> = {
    productive: "Productive",
    nonProductive: "Non-Prod",
    neutral: "Neutral",
  };

  const currentKey = current.dataKey as ProductivityKey;

  return (
    <div className="rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 shadow-lg">
      <div className="mb-2 text-[12px] font-medium text-[#101828]">
        {label}
      </div>

      <div className="flex items-center gap-2">
        <span
          className="h-3 w-3 rounded-full"
          style={{
            backgroundColor: colorMap[currentKey],
          }}
        />

        <span className="text-[12px] text-[#344054]">
          {labelMap[currentKey]}:
        </span>

        <span className="text-[12px] font-semibold text-[#101828]">
          {current.value}%
        </span>
      </div>
    </div>
  );
};

const HourlyProductivityChart = ({ data }: Props) => {
  const [hovered, setHovered] = useState<HoveredState>({
    index: null,
    key: null,
  });

  const productiveAvg =
    data.length > 0
      ? Math.round(
          data.reduce((sum, item) => sum + item.productive, 0) / data.length,
        )
      : 0;

  const nonProductiveAvg =
    data.length > 0
      ? Math.round(
          data.reduce((sum, item) => sum + item.nonProductive, 0) /
            data.length,
        )
      : 0;

  const neutralAvg =
    data.length > 0
      ? Math.round(
          data.reduce((sum, item) => sum + item.neutral, 0) / data.length,
        )
      : 0;

  const target = data.length > 0 ? data[0].target : 80;

  return (
    <div className="rounded-[18px] border border-[#D0D5DD] bg-white px-5 pb-5 pt-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="mb-4 flex items-center gap-4">
          <h3 className="whitespace-nowrap text-[15px] font-semibold text-[#002B5B]">
            Hourly Productivity · Today
          </h3>

          <div className="rounded-full bg-[#EEF4FF] px-3 py-[4px] text-[11px] font-medium text-[#2563EB]">
            Productive {productiveAvg}% · Non-Prod {nonProductiveAvg}% · Neutral{" "}
            {neutralAvg}% time per hour
          </div>
        </div>
      </div>

      <CustomLegend hoveredKey={hovered.key} setHovered={setHovered} />

      <div className="h-[215px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 35,
              left: 10,
              bottom: 0,
            }}
          >
            <CartesianGrid
              stroke="#EAECF0"
              strokeDasharray="3 3"
              vertical={false}
            />

            <XAxis
              interval={0}
              tickMargin={12}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 10,
                fill: "#475467",
              }}
            />

            <YAxis
              domain={[0, 100]}
              width={34}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 10,
                fill: "#344054",
              }}
              tickFormatter={(value: number) => `${value}%`}
            />

            <Tooltip content={<CustomTooltip hovered={hovered} />} />

            <ReferenceLine
              y={target}
              stroke="#adbfc7ff"
              strokeWidth={2}
              strokeDasharray="6 4"
              ifOverflow="extendDomain"
              label={({ viewBox }) => {
                if (!viewBox) return null;

                const { x, y, width } = viewBox;

                return (
                  <g>
                    <rect
                      x={(x ?? 0) + (width ?? 0) - 72}
                      y={(y ?? 0) - 22}
                      width={68}
                      height={20}
                      rx={4}
                      fill="#ffffff9d"
                      stroke="#2563EB"
                    />
                    <text
                      x={(x ?? 0) + (width ?? 0) - 38}
                      y={(y ?? 0) - 6}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#2563EB"
                    >
                      {target}% target
                    </text>
                  </g>
                );
              }}
            />

            <Bar
              dataKey="productive"
              stackId="time"
              barSize={50}
              onMouseOver={(_, index) =>
                setHovered({
                  index,
                  key: "productive",
                })
              }
              onMouseOut={() =>
                setHovered({
                  index: null,
                  key: null,
                })
              }
            >
              {data.map((_, index) => (
                <Cell
                  key={index}
                  fill="#22C55E"
                  fillOpacity={
                    hovered.key === null
                      ? 1
                      : hovered.key === "productive"
                        ? 1
                        : 0.25
                  }
                />
              ))}
            </Bar>

            <Bar
              dataKey="nonProductive"
              stackId="time"
              barSize={50}
              onMouseOver={(_, index) =>
                setHovered({
                  index,
                  key: "nonProductive",
                })
              }
              onMouseOut={() =>
                setHovered({
                  index: null,
                  key: null,
                })
              }
            >
              {data.map((_, index) => (
                <Cell
                  key={index}
                  fill="#EF4444"
                  fillOpacity={
                    hovered.key === null
                      ? 1
                      : hovered.key === "nonProductive"
                        ? 1
                        : 0.25
                  }
                />
              ))}
            </Bar>

            <Bar
              dataKey="neutral"
              stackId="time"
              barSize={50}
              onMouseOver={(_, index) =>
                setHovered({
                  index,
                  key: "neutral",
                })
              }
              onMouseOut={() =>
                setHovered({
                  index: null,
                  key: null,
                })
              }
            >
              {data.map((_, index) => (
                <Cell
                  key={index}
                  fill="#94A3B8"
                  fillOpacity={
                    hovered.key === null
                      ? 1
                      : hovered.key === "neutral"
                        ? 1
                        : 0.25
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HourlyProductivityChart;
